<template>
  <div class="language-selector" :class="`theme-${mode}`">
    <div ref="triggerRef" class="language-trigger" @click="toggleDropdown">
      <span class="current-language">
        {{ currentLanguageInfo.flag }} {{ currentLanguageInfo.name }}
      </span>
      <svg viewBox="0 0 24 24" width="16" height="16" class="dropdown-icon" :class="{ 'rotated': showDropdown }">
        <path fill="currentColor" d="M7 10l5 5 5-5z" />
      </svg>
    </div>

    <Teleport to="body">
      <div v-if="showDropdown" class="language-dropdown" :style="dropdownStyle" @click.stop>
        <div v-for="language in availableLanguages" :key="language.code" class="language-option"
          :class="{ 'active': language.code === currentLocale }" @click="selectLanguage(language.code)">
          <span class="language-flag">{{ language.flag }}</span>
          <span class="language-name">{{ language.name }}</span>
          <svg v-if="language.code === currentLocale" viewBox="0 0 24 24" width="16" height="16" class="check-icon">
            <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
          </svg>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, ref, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { getAvailableLocales, setLocale, getCurrentLocale } from '@/i18n.js'

export default {
  name: 'LanguageSelector',
  props: {
    mode: {
      type: String,
      default: 'dark',
      validator: (value) => ['dark', 'light'].includes(value)
    }
  },
  setup(props) {
    const { locale } = useI18n()
    const showDropdown = ref(false)
    const availableLanguages = getAvailableLocales()
    const triggerRef = ref(null)

    const currentLocale = computed(() => getCurrentLocale())

    const currentLanguageInfo = computed(() => {
      return availableLanguages.find(lang => lang.code === currentLocale.value) || availableLanguages[0]
    })

    const dropdownStyle = ref({})

    const updateDropdownPosition = () => {
      if (triggerRef.value) {
        const rect = triggerRef.value.getBoundingClientRect()
        dropdownStyle.value = {
          position: 'fixed',
          top: `${rect.bottom + 4}px`,
          right: `${window.innerWidth - rect.right}px`,
          zIndex: 2500
        }
      }
    }

    const toggleDropdown = async () => {
      showDropdown.value = !showDropdown.value
      if (showDropdown.value) {
        await nextTick()
        updateDropdownPosition()
      }
    }

    const selectLanguage = (languageCode) => {
      if (languageCode !== currentLocale.value) {
        const selectedLang = availableLanguages.find(lang => lang.code === languageCode)
        setLocale(languageCode)
        // 使用简单的通知消息，避免复杂的翻译逻辑
        window.$notify?.success(`Language switched to ${selectedLang?.name}`)
      }
      showDropdown.value = false
    }

    const closeDropdown = () => {
      showDropdown.value = false
    }

    // 点击外部关闭下拉菜单
    const handleClickOutside = (event) => {
      if (!event.target.closest('.language-selector')) {
        closeDropdown()
      }
    }

    onMounted(() => {
      document.addEventListener('click', handleClickOutside)
    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
    })

    return {
      showDropdown,
      availableLanguages,
      currentLocale,
      currentLanguageInfo,
      toggleDropdown,
      selectLanguage,
      closeDropdown,
      triggerRef,
      dropdownStyle
    }
  }
}
</script>

<style scoped>
.language-selector {
  position: relative;
}

.language-trigger {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius-large);
  cursor: pointer;
  transition: all 0.2s ease;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 500;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.7);
}

.language-trigger:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.current-language {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  overflow: visible;
}

.dropdown-icon {
  color: rgba(255, 255, 255, 0.7);
  transition: transform 0.2s ease;
}

.dropdown-icon.rotated {
  transform: rotate(180deg);
}

.language-dropdown {
  background-color: var(--color-surface);
  border: 1px solid var(--color-outline-variant);
  border-radius: var(--border-radius-large);
  box-shadow: var(--shadow-3);
  min-width: 160px;
  overflow: hidden;
}

.language-option {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  color: var(--color-on-surface);
  font-size: 14px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.language-option:hover {
  background-color: var(--color-surface-variant);
}

.language-option.active {
  background-color: var(--color-primary-container);
  color: var(--color-on-primary-container);
}

.language-flag {
  font-size: 16px;
}

.language-name {
  flex: 1;
  font-weight: 500;
}

.check-icon {
  color: var(--color-primary);
}

/* 浅色主题样式 */
.language-selector.theme-light .language-trigger {
  color: var(--color-on-surface);
  text-shadow: none;
  border: 1px solid var(--color-outline-variant);
  background-color: var(--color-surface);
}

.language-selector.theme-light .language-trigger:hover {
  background-color: var(--color-surface-variant);
  color: var(--color-on-surface);
  border-color: var(--color-outline);
}

.language-selector.theme-light .dropdown-icon {
  color: var(--color-on-surface-variant);
}

.language-selector.theme-light .language-trigger:hover .dropdown-icon {
  color: var(--color-on-surface);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .language-trigger {
    padding: var(--spacing-xs) var(--spacing-sm);
    font-size: 13px;
  }

  .language-dropdown {
    min-width: 140px;
  }

  .language-option {
    padding: var(--spacing-xs) var(--spacing-sm);
    font-size: 13px;
  }
}
</style>